import styled from '@emotion/styled'
import { keyBy } from 'lodash'
import { ReactNode } from 'react'

import { Dayjs } from '@bemlo/date'
import { Locale } from '@bemlo/enums'

import { Text } from './Text'
import { Tooltip } from './Tooltip'
import { Color, theme } from '../constants'
import { Intent } from '../constants/intent'
import { useTranslations } from '../translate'

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto repeat(52, 1fr)',
  gridTemplateRows: 'auto repeat(7, 1fr)',
  columnGap: theme.spacing['0.5'],
  rowGap: theme.spacing['0.5'],
  padding: theme.spacing['2'],
  width: 'fit-content',
})

type DayProps = {
  column: number
  row: number
  backgroundColor?: Color
}

const labelProps = {
  color: Color.DARKEST_MEDIUM_GREY,
  fontSize: 11,
  fontWeight: theme.font.weight.bold,
  userSelect: 'none',
} as const

const ColumnLabel = styled(Text)<{ week: number }>(({ week }) => ({
  paddingLeft: week < 10 ? theme.spacing['0.5'] : undefined,
  gridRow: 1,
  gridColumn: `${week + 1} / ${week + 5}`,
  ...labelProps,
}))

const RowLabel = styled(Text)<{ day: number }>(({ day }) => ({
  paddingRight: theme.spacing['1'],
  gridColumn: 1,
  gridRow: day + 1,
  ...labelProps,
}))

const Day = styled.div<DayProps>(({ column, row, backgroundColor }) => ({
  gridColumn: column,
  gridRow: row,
  height: theme.spacing['2.5'],
  width: theme.spacing['2.5'],
  backgroundColor: backgroundColor ?? Color.MEDIUM_GREY,
  borderRadius: theme.border.radius.small,
}))

const useWeeks = (locale: Locale) =>
  [Locale.SV, Locale.DA, Locale.FI, Locale.NB].includes(locale)

const absoluteWeek = (yearStart: Dayjs, date: Dayjs) => {
  const dayOfYear = date.diff(yearStart, 'day') + 1

  // The offset is the difference in days from Monday (1) if January 1st is not a Monday
  const offset = yearStart.day() === 0 ? 6 : yearStart.day() - 1

  // Adjust the day of the year based on the offset
  const adjustedDayOfYear = dayOfYear + offset

  // Calculate the week number
  return Math.ceil(adjustedDayOfYear / 7)
}

export type ActivityPoint = {
  date: string
  color: Color
  tooltip?: {
    intent: Intent
    content: ReactNode
  }
}

type ActivityCalendarProps = {
  // YYYY-MM-DD
  year: string
  activity: ActivityPoint[]
}

export const ActivityCalendar = ({ year, activity }: ActivityCalendarProps) => {
  const t = useTranslations()

  const startDate = t.$date(year, 'YYYY')
  const shiftMap = keyBy(activity, 'date')

  const days = (() => {
    const days: ReactNode[] = []

    for (let i = 0; i < 366; i++) {
      const day = startDate.add(i, 'day')
      const date = day.format('YYYY-MM-DD')

      if (date === 'Invalid Date') continue
      const week = absoluteWeek(startDate, day)

      // If it is not a leap year, skip the 366th day
      if (i === 365 && day.format('YYYY') !== year) continue

      const info: ActivityPoint | undefined = shiftMap[date]

      days.push(
        <Tooltip key={date} text={info?.tooltip ? undefined : date}>
          {info?.tooltip && (
            <Tooltip.Content intent={info.tooltip.intent}>
              {info.tooltip.content}
            </Tooltip.Content>
          )}
          <Tooltip.Trigger asChild>
            <Day
              column={week + 1}
              row={day.isoWeekday() + 1}
              backgroundColor={info ? info.color : undefined}
            />
          </Tooltip.Trigger>
        </Tooltip>,
      )
    }

    return days
  })()

  return (
    <Grid>
      {Array.from({ length: 7 }).map((_, i) => (
        <RowLabel key={`day-${i}`} day={i + 1}>
          {startDate.startOf('week').add(i, 'day').format('dd')}
        </RowLabel>
      ))}
      {useWeeks(t.$locale)
        ? Array.from({ length: 18 }).map((_, i) => {
            return (
              <ColumnLabel key={i} week={i * 3 + 1}>
                {startDate.add(i * 3, 'week').format('W')}
              </ColumnLabel>
            )
          })
        : Array.from({ length: 6 }).map((_, i) => {
            return (
              <ColumnLabel key={i} week={i * 9 + 1}>
                {startDate.add(i * 9, 'week').format('MMM')}
              </ColumnLabel>
            )
          })}
      {days}
    </Grid>
  )
}
